
/**
 * 🌍 IntelOptics Geography Utilities
 * Centralizes ISO normalization and deterministic positioning for the global risk map.
 */

/**
 * Normalizes various country identifiers to standard ISO3 (Uppercase).
 * Handles ISO2 fallback if needed (though user says not present, good for safety).
 */
export const normalizeISO3 = (code: string | undefined | null): string => {
    if (!code) return 'UNK';
    const trimmed = code.trim().toUpperCase();
    
    // Common mappings if we ever encounter ISO2 or custom IDs
    const iso2ToIso3: Record<string, string> = {
        'UA': 'UKR',
        'IL': 'ISR',
        'US': 'USA',
        'GB': 'GBR',
        'RU': 'RUS',
        'CN': 'CHN',
        // Add more as discovered
    };

    if (trimmed.length === 2) {
        return iso2ToIso3[trimmed] || trimmed;
    }
    
    return trimmed;
};

/**
 * Calculates a deterministic offset (Spiral) based on an index.
 * This prevents markers from overlapping while ensuring they stay in the same
 * position on every refresh.
 * 
 * @param index The index of the event for a specific country
 * @param baseRadius The step size for the spiral (degrees)
 */
export const getDeterministicOffset = (index: number, baseRadius: number = 0.05) => {
    if (index === 0) return { lat: 0, lng: 0 };
    
    // Use Golden Angle for spiral distribution
    const angle = index * 137.5 * (Math.PI / 180);
    const radius = baseRadius * Math.sqrt(index);
    
    return {
        lat: radius * Math.sin(angle),
        lng: radius * Math.cos(angle)
    };
};

/**
 * Hashes a string into a numeric value for deterministic grouping.
 */
export const simpleHash = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
};
